<!-- biome-ignore-all lint/a11y/noStaticElementInteractions: the group role is bound dynamically to radiogroup/group -->
<script setup lang="ts">
  import { computed, provide, shallowReactive } from 'vue';
  import { SelectionCardMode } from '../SelectionCard/SelectionCard.types';
  import {
    SELECTION_CARD_GROUP_CONTEXT_KEY,
    type SelectionCardRegistration,
  } from './SelectionCardGroup.context';
  import type {
    SelectionCardGroupEmits,
    SelectionCardGroupProps,
    SelectionCardGroupSlots,
    SelectionCardValue,
  } from './SelectionCardGroup.types';

  const props = withDefaults(defineProps<SelectionCardGroupProps>(), {
    modelValue: null,
    mode: SelectionCardMode.SINGLE,
    disabled: false,
  });

  const emit = defineEmits<SelectionCardGroupEmits>();

  defineSlots<SelectionCardGroupSlots>();

  const registry = shallowReactive(
    new Map<string, SelectionCardRegistration>(),
  );

  const isMultiple = computed(() => props.mode === SelectionCardMode.MULTIPLE);

  const selectedValues = computed<SelectionCardValue[]>(() => {
    if (Array.isArray(props.modelValue)) return props.modelValue;
    return props.modelValue === null ? [] : [props.modelValue];
  });

  const isSelected = (value: SelectionCardValue): boolean =>
    selectedValues.value.includes(value);

  const isEnabled = (entry: SelectionCardRegistration): boolean =>
    !props.disabled && !entry.disabled();

  const tabStopId = computed<string | null>(() => {
    if (isMultiple.value || props.disabled) return null;
    let first: string | null = null;
    for (const [id, entry] of registry) {
      const value = entry.value();
      if (value === undefined || entry.disabled()) continue;
      if (isSelected(value)) return id;
      first ??= id;
    }
    return first;
  });

  const select = (value: SelectionCardValue): void => {
    if (props.disabled) return;
    if (isMultiple.value) {
      emit(
        'update:modelValue',
        isSelected(value)
          ? selectedValues.value.filter((entry) => entry !== value)
          : [...selectedValues.value, value],
      );
      return;
    }
    if (isSelected(value)) return;
    emit('update:modelValue', value);
  };

  provide(SELECTION_CARD_GROUP_CONTEXT_KEY, {
    mode: computed(() => props.mode),
    disabled: computed(() => props.disabled),
    tabStopId,
    isSelected,
    select,
    register: (id, registration) => {
      registry.set(id, registration);
    },
    unregister: (id) => {
      registry.delete(id);
    },
  });

  const ARROW_DIRECTIONS: Record<string, 1 | -1> = {
    ArrowDown: 1,
    ArrowRight: 1,
    ArrowUp: -1,
    ArrowLeft: -1,
  };

  type OrderedEntry = {
    entry: SelectionCardRegistration;
    element: HTMLElement;
  };

  const orderedEnabledEntries = (): OrderedEntry[] =>
    [...registry.values()]
      .flatMap((entry) => {
        const element = entry.element();
        return element && isEnabled(entry) ? [{ entry, element }] : [];
      })
      .sort((a, b) =>
        a.element.compareDocumentPosition(b.element) &
        Node.DOCUMENT_POSITION_FOLLOWING
          ? -1
          : 1,
      );

  function handleKeydown(event: KeyboardEvent): void {
    if (isMultiple.value || props.disabled) return;
    const direction = ARROW_DIRECTIONS[event.key];
    if (!direction) return;
    const entries = orderedEnabledEntries();
    const current = entries.findIndex(
      ({ element }) => element === event.target,
    );
    if (current === -1) return;
    event.preventDefault();
    const next =
      entries[(current + direction + entries.length) % entries.length];
    if (next === entries[current]) return;
    next.element.focus();
    const value = next.entry.value();
    if (value !== undefined) select(value);
  }

  const resolvedRole = computed(() =>
    isMultiple.value ? 'group' : 'radiogroup',
  );
</script>

<template>
  <div
    :role="resolvedRole"
    :aria-label="ariaLabel"
    :aria-labelledby="ariaLabelledby"
    :aria-disabled="disabled || undefined"
    :data-testid="testId"
    @keydown="handleKeydown"
  >
    <slot />
  </div>
</template>
